import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { post } from "../../lib/api";
import { KEY_LABEL_MAX } from "../../lib/apps";
import { since, timestamp } from "../../lib/format";
import type { AppDetail, AppKey, IssuedAppKey } from "../../lib/types";
import { Button } from "../ui/Button";
import { CopyButton } from "../ui/Copy";
import { EmptyState, ErrorNotice } from "../ui/Feedback";
import { Field } from "../ui/Input";
import { Pill } from "../ui/Status";
import { Cell, TableFrame, TableHead, TableRow } from "../ui/Table";
import type { Column } from "../ui/Table";
import { MicroLabel, SectionTitle } from "../ui/Text";

/**
 * An app's API keys: issue, read once, revoke.
 *
 * An app may hold several live keys so a partner can rotate without a gap —
 * issue the new one, deploy it, revoke the old. The table keeps revoked and
 * expired keys in view, because "which key was that?" is the question a log line
 * with only a prefix in it raises.
 */

const COLUMNS: readonly Column[] = [
  { key: "prefix", label: "Key", width: "22%" },
  { key: "label", label: "Label", width: "20%" },
  { key: "status", label: "Status", width: "12%" },
  { key: "lastUsed", label: "Last used", width: "14%" },
  { key: "expires", label: "Expires", width: "16%" },
  { key: "actions", label: "" },
];

const STATUS_TONE = { active: "available", revoked: "faulted", expired: "idle" } as const;

export interface AppKeysProps {
  readonly app: AppDetail;
  readonly timeZone: string;
  readonly canManage: boolean;
  readonly onChanged: (message: string) => void;
}

export function AppKeys({ app, timeZone, canManage, onChanged }: AppKeysProps) {
  const [isIssuing, setIsIssuing] = useState(false);
  const [issued, setIssued] = useState<IssuedAppKey | undefined>(undefined);

  return (
    <section aria-labelledby="app-keys" className="flex flex-col gap-4">
      <SectionTitle
        action={
          canManage && !isIssuing && issued === undefined ? (
            <Button variant="primary" onClick={() => setIsIssuing(true)}>
              Issue key
            </Button>
          ) : undefined
        }
      >
        <span id="app-keys">Keys</span>
      </SectionTitle>

      {issued === undefined ? null : (
        <IssuedKeyPanel issued={issued} onDismiss={() => setIssued(undefined)} />
      )}

      {isIssuing ? (
        <IssueKeyForm
          appId={app.id}
          onIssued={(result) => {
            setIsIssuing(false);
            setIssued(result);
            onChanged(`Key ${result.key.prefix} issued.`);
          }}
          onCancel={() => setIsIssuing(false)}
        />
      ) : null}

      {app.keys.length === 0 ? (
        <EmptyState
          title="No keys."
          detail="Nothing can call the API as this app until it has one."
        />
      ) : (
        <TableFrame>
          <TableHead columns={COLUMNS} />
          <tbody>
            {app.keys.map((key, index) => (
              <KeyRow
                key={key.id}
                appId={app.id}
                appKey={key}
                index={index}
                timeZone={timeZone}
                canManage={canManage}
                onRevoked={() => onChanged(`Key ${key.prefix} revoked. It stops working at once.`)}
              />
            ))}
          </tbody>
        </TableFrame>
      )}
    </section>
  );
}

function KeyRow({
  appId,
  appKey,
  index,
  timeZone,
  canManage,
  onRevoked,
}: {
  readonly appId: string;
  readonly appKey: AppKey;
  readonly index: number;
  readonly timeZone: string;
  readonly canManage: boolean;
  readonly onRevoked: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const revoke = useMutation({
    mutationFn: () => post<AppKey>(`/apps/${appId}/keys/${appKey.id}/revoke`),
    onSuccess: onRevoked,
  });

  return (
    <TableRow index={index}>
      <Cell className="font-mono text-micro">ocpp_{appKey.prefix}_…</Cell>
      <Cell muted={appKey.label === null}>{appKey.label ?? "—"}</Cell>
      <Cell>
        <Pill tone={STATUS_TONE[appKey.status]}>{appKey.status}</Pill>
      </Cell>
      <Cell muted>{appKey.lastUsedAt === null ? "never" : since(appKey.lastUsedAt)}</Cell>
      <Cell muted>
        {appKey.expiresAt === null ? "never" : timestamp(appKey.expiresAt, timeZone)}
      </Cell>
      <Cell>
        {revoke.isError ? <ErrorNotice error={revoke.error} /> : null}
        {!canManage || appKey.status !== "active" ? null : confirming ? (
          <span className="flex flex-wrap items-center gap-2">
            <Button
              variant="danger"
              pending={revoke.isPending}
              aria-label={`Confirm revoking ${appKey.prefix}`}
              onClick={() => revoke.mutate()}
            >
              Revoke now
            </Button>
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              Keep
            </Button>
          </span>
        ) : (
          <Button
            variant="ghost"
            aria-label={`Revoke ${appKey.prefix}`}
            onClick={() => setConfirming(true)}
          >
            Revoke
          </Button>
        )}
      </Cell>
    </TableRow>
  );
}

function IssueKeyForm({
  appId,
  onIssued,
  onCancel,
}: {
  readonly appId: string;
  readonly onIssued: (issued: IssuedAppKey) => void;
  readonly onCancel: () => void;
}) {
  const [label, setLabel] = useState("");
  const [expires, setExpires] = useState("");

  const issue = useMutation({
    mutationFn: () =>
      post<IssuedAppKey>(`/apps/${appId}/keys`, {
        label: label.trim() || null,
        // A date input's value is a calendar day; the key lasts to the end of it, browser-local.
        expiresAt: expires === "" ? null : new Date(`${expires}T23:59:59`).toISOString(),
      }),
    onSuccess: onIssued,
  });

  return (
    <div className="flex flex-col gap-4 border border-rule bg-paper px-4 py-4">
      <div className="flex items-baseline justify-between gap-4">
        <MicroLabel>New key</MicroLabel>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      {issue.isError ? <ErrorNotice error={issue.error} /> : null}
      <form
        aria-label="New key"
        onSubmit={(event) => {
          event.preventDefault();
          issue.mutate();
        }}
        className="grid grid-cols-1 gap-x-6 gap-y-5 md:grid-cols-2"
      >
        <Field
          label="Label"
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          placeholder="Optional — e.g. production"
          maxLength={KEY_LABEL_MAX}
          autoFocus
        />
        <Field
          label="Expires"
          type="date"
          value={expires}
          onChange={(event) => setExpires(event.target.value)}
          hint="Leave empty for a key that lasts until revoked."
        />
        <div className="flex items-end md:col-span-2">
          <Button type="submit" variant="primary" pending={issue.isPending}>
            Issue key
          </Button>
        </div>
      </form>
    </div>
  );
}

/**
 * The only sight of a key. Stored as a hash, like a station's password, so the
 * panel stays until it is dismissed on purpose — see `StationCredential`.
 */
function IssuedKeyPanel({
  issued,
  onDismiss,
}: {
  readonly issued: IssuedAppKey;
  readonly onDismiss: () => void;
}) {
  return (
    <section
      role="alert"
      aria-labelledby="issued-key-heading"
      className="flex flex-col gap-4 border-l-2 border-signal bg-paper-raised px-4 py-4"
    >
      <div className="flex items-baseline justify-between gap-4">
        <h2
          id="issued-key-heading"
          className="m-0 text-micro font-semibold uppercase tracking-[0.11em] text-ink"
        >
          Key issued
        </h2>
        <Button variant="ghost" onClick={onDismiss}>
          I have saved it
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <code
          data-testid="issued-app-key"
          className="select-all break-all bg-paper px-2 py-1 font-mono text-data text-ink"
        >
          {issued.token}
        </code>
        <CopyButton value={issued.token} what="API key" />
      </div>
      <p className="m-0 max-w-prose text-body text-ink-muted">
        Send this to the partner now, over a channel you trust. It is stored only as a hash and
        cannot be shown again; if it is lost, issue another and revoke this one. They send it as{" "}
        <span className="font-mono">Authorization: Bearer …</span>.
      </p>
    </section>
  );
}
